import React, { ComponentProps } from 'react';
import { Box, Rating, Typography } from '@mui/material';
import { Info, StarIcon } from '@phosphor-icons/react';
import { GetDefaultPlansYear, tooltips } from '@coverright/utils';
import { Tooltip } from '@coverright/ui/inputs';
import { PlanYear } from '@coverright/data-access/types/medicare';

interface StarRatingProps {
  plan?: { rating?: number | null, ratingNote?: string | null, planYear?: PlanYear | null; },
  size?: number,
  iconFilledProps?: ComponentProps<typeof StarIcon>,
  iconEmptyProps?: ComponentProps<typeof StarIcon>,
  endAdornment?: JSX.Element,
  labelClassName?: string,
  hideTooltip?: boolean,
}

// Aetna 2027 preview (aetna-marketplace toggle) serves 2027 plan data labelled as the current year.
export function getStarRatingYear(plan?: { planYear?: PlanYear | null }): string {
  try {
    if (localStorage.getItem('cr:aetna-plan-data-source') === 'aetna-2027') {
      return '2027';
    }
  } catch (e) {
    // localStorage unavailable (SSR / privacy mode)
  }
  return (plan?.planYear || GetDefaultPlansYear()).toString().replace('Year', '');
}

export function StarRating({plan, size = 18, endAdornment, iconFilledProps = {}, iconEmptyProps = {}, labelClassName = 'fs-12', hideTooltip}: StarRatingProps) {
  if (!plan || (!plan.rating && !plan.ratingNote)) {
    return null;
  }
  const planYear = getStarRatingYear(plan);

  if (!endAdornment) {
    endAdornment = <Box sx={{ display: 'flex', gap: 0.5 }}>
      {!!plan?.rating && (
        <Typography sx={{ ml: 1, mt: 0.25, lineHeight: 1, fontSize: 14 }}>
          ({planYear})
        </Typography>
      )}
      <Info size={16} color={'#7b8f95'} weight={'bold'} />
    </Box>
  }

  const rating = !plan.rating ? <Typography sx={{color: '#4D4D4D'}} className={`no-wrap medium ${labelClassName || ''}`} >{plan.ratingNote}</Typography>
    : <Rating emptyIcon={<StarIcon size={size} style={{minWidth: size}} color={"#1e92a0"} weight="duotone" {...iconEmptyProps} />}
              icon={<StarIcon size={size} style={{minWidth: size}} color={"#1e92a0"} weight="fill" {...iconFilledProps} />}
              defaultValue={plan.rating || 0}
              precision={.5}
              readOnly
    />;

  return <Tooltip arrow title={hideTooltip ? '' : tooltips.starRating}>
    <Box sx={{width: 'fit-content'}} className={'flex flex-align-center'}>
      {rating}
      {endAdornment}
    </Box>
  </Tooltip>;
}
